/**
 * ClosingStrip — shows a countdown to the next closing date.
 * Color-coded by urgency: green > 7d, yellow 3-7d, red < 3d.
 * Clicking navigates to the deals tab.
 */

import { useEffect } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { route } from 'preact-router';
import { api } from '../core/api/client';
import type { NextClosing } from '../core/api/types';
import { chicagoToday, chicagoYMD, parseIsoAnchoredToChicago } from '../ui/timezone';
import styles from './ClosingStrip.module.css';

const nextClosing = signal<NextClosing | null>(null);

/** Refresh the next closing from the API */
export async function refreshNextClosing() {
  try {
    const res = await api.getNextClosing();
    nextClosing.value = res.closing;
  } catch {
    nextClosing.value = null;
  }
}

/** BannerSlot coordination (P1a): mirrors this banner's own visibility rule.
 *  BannerSlot triggers refreshNextClosing() itself on mount, since this strip
 *  is no longer mounted (and thus can't self-fetch) until data exists. */
export const closingStripWantsToShow = computed(() => nextClosing.value !== null);

function getUrgencyClass(daysOut: number | null): string {
  if (daysOut === null) return styles.normal;
  if (daysOut <= 0) return styles.today;
  if (daysOut < 3) return styles.urgent;
  if (daysOut <= 7) return styles.soon;
  return styles.normal;
}

function formatCountdown(daysOut: number | null): string {
  if (daysOut === null) return '';
  if (daysOut <= 0) return 'TODAY';
  if (daysOut === 1) return '1 day';
  return `${daysOut} days`;
}

function daysUntilClosing(closingDate: string): number | null {
  const targetDate = parseIsoAnchoredToChicago(closingDate);
  if (Number.isNaN(targetDate.getTime())) return null;

  const target = chicagoYMD(targetDate);
  const today = chicagoToday();
  const targetDay = Date.UTC(target.year, target.month - 1, target.day);
  const todayDay = Date.UTC(today.year, today.month - 1, today.day);
  return Math.round((targetDay - todayDay) / 86_400_000);
}

export function ClosingStrip() {
  useEffect(() => {
    refreshNextClosing();
  }, []);

  const closing = nextClosing.value;
  if (!closing) return null;

  const daysOut = daysUntilClosing(closing.closing_date);

  return (
    <div class={styles.strip} onClick={() => route('/deals')}>
      {/* Calendar icon */}
      <svg class={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>

      <span class={styles.address}>{closing.property_address}</span>

      <span class={styles.client}>{closing.client_name}</span>

      <span class={`${styles.countdown} ${getUrgencyClass(daysOut)}`}>
        {formatCountdown(daysOut)}
      </span>
    </div>
  );
}
